import { Column } from "@tento-ui/component-builtin";
import type { BlogCategory } from "../blog-data";
import type {
  BlogIndexRoute,
  BlogPage,
} from "../blog-variant";
import "@tento-ui/brand/blog/blog-search-filter-bar";
import "@tento-ui/brand/blog/blog-simple-list-view";
import "@tento-ui/brand/blog/blog-list-pagelet";
import { BlogListPagelet } from "@tento-ui/brand/blog/blog-list-pagelet";
import { BlogSearchFilterBar } from "@tento-ui/brand/blog/blog-search-filter-bar";
import { BlogSimpleListView } from "@tento-ui/brand/blog/blog-simple-list-view";

// Category filter pills shown below the search bar (BlogSearchFilterBar renders
// one .filter-pill per entry; an empty list hides the row).
const categories: BlogCategory[] = [
  "product",
  "research",
  "engineering",
  "company",
  "announcements",
];

// Show the search bar only once the blog has at least this many posts. Keyed off
// the unfiltered corpus total (props.totalPosts), so the bar stays put while the
// user filters by category/search instead of vanishing on a short result set.
const SEARCH_BAR_MIN_ENTRIES = 10;

let $searchFilter: Option<BlogSearchFilterBar> = null;
let $listView: Option<BlogSimpleListView> = null;

export const listRoute: BlogIndexRoute = {
  make(props: BlogPage.Props, delegate: BlogPage.Delegate): HTMLElement {
    $searchFilter = BlogSearchFilterBar.makeWith({
      categories,
      activeCategory: props.categoryFilter,
      onSearch: delegate.onSearch,
      onFilterCategory: delegate.onFilterCategory,
    });
    $listView = BlogSimpleListView.makeWith(makeListViewProps(props, delegate));
    updateSearchVisibility(props.totalPosts);
    return BlogListPagelet.makeWith({
      hero: makeHero(),
      searchFilter: $searchFilter,
      listView: $listView,
    });
  },
  bind(pagelet: HTMLElement, props: BlogPage.Props, delegate: BlogPage.Delegate): HTMLElement {
    $searchFilter?.updateActiveCategory(props.categoryFilter);
    $listView?.bind(makeListViewProps(props, delegate));
    updateSearchVisibility(props.totalPosts);
    return pagelet;
  },
};

// Stable search-bar visibility: shown iff the blog has enough posts to be worth
// searching. `.hidden` drives BlogSearchFilterBar's `:host(.hidden){display:none}`.
function updateSearchVisibility(totalPosts: number): void {
  $searchFilter?.classList.toggle("hidden", totalPosts < SEARCH_BAR_MIN_ENTRIES);
}

function makeListViewProps(
  props: BlogPage.Props,
  delegate: BlogPage.Delegate,
): BlogSimpleListView.Props {
  return {
    posts: props.posts,
    displayCount: props.displayCount,
    searchQuery: props.searchQuery,
    onSeeMore: delegate.onSeeMore,
    onCardClick: delegate.onCardClick,
  };
}

function makeHero(): HTMLElement {
  const $hero = Column.make();
  $hero.classList.add("hero");
  $hero.assignStyles({
    padding: "80px 40px 40px",
    paddingTop: "80px",
    borderBottom: "1px solid var(--divider)",
    alignItems: "start",
  });

  // Layout (the shared date-rail grid + its mobile collapse) lives in the blog
  // tokens: ui-loaf `.hero-container` / `.hero-content`. The hero text sits in the
  // content column so it aligns with the post titles, and collapses to a single
  // column on mobile.
  const $container = Column.make();
  $container.classList.add("hero-container");

  const $content = Column.make();
  $content.classList.add("hero-content");

  const $title = document.createElement("h1");
  $title.className = "hero-title";
  $title.textContent = "Blog";

  const $subtitle = document.createElement("p");
  $subtitle.className = "hero-subtitle";
  $subtitle.textContent = "Launch notes from Lona.";

  $content.append($title, $subtitle);
  $container.appendChild($content);
  $hero.appendChild($container);
  return $hero;
}
